import { Modal, Box, Button, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";

import { modalStyle } from "../constants";
import { formatAddress, sendGetRequest } from "../../utils";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-CA") : "—";

const formatMoney = (cents, currency) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: (currency || "cad").toUpperCase(),
  }).format(cents / 100);

const Field = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div>{children || "—"}</div>
  </div>
);

// Read-only view of a deleted member: the archived record, plus their
// Stripe invoices fetched live.
const ArchivedDetailsModal = ({ open, handleClose, user }) => {
  const hasStripe = Boolean(user.subscription_id || user.customer_id);
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!hasStripe) return undefined;
    let cancelled = false;
    sendGetRequest(`archived-customers/${user.id}/stripe`, true)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.errMessage) setError(data.errMessage);
        else setInvoices(data.invoices);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load Stripe invoices.");
      });
    return () => {
      cancelled = true;
    };
  }, [hasStripe, user.id]);

  const address = formatAddress(user);

  return (
    <Modal open={open} onClose={handleClose} className="relative">
      <Box sx={{ ...modalStyle, width: 560, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
        <h2 className="text-xl font-semibold">{user.name}</h2>
        <p className="text-sm text-gray-500 mb-4">
          Deleted by {user.deleted_by || "unknown"} on{" "}
          {formatDate(user.deleted_date)}
        </p>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <Field label="Email">{user.email}</Field>
          <Field label="Phone">{user.phone_number}</Field>
          <Field label="Address">
            {address.line1}
            <br />
            {address.line2}
          </Field>
          <Field label="Member since">{formatDate(user.created_date)}</Field>
          <Field label="Subscription">{user.subscription_id}</Field>
          <Field label="Customer">{user.customer_id}</Field>
        </div>

        <h3 className="mt-6 mb-2 font-semibold">Stripe invoices</h3>
        {!hasStripe ? (
          <p className="text-sm text-gray-600">
            This member paid manually, so there is no Stripe data.
          </p>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : invoices === null ? (
          <p className="text-sm text-gray-600">Loading…</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-gray-600">No Stripe invoices found.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="border-t">
                  <td className="py-1.5">{formatDate(invoice.created * 1000)}</td>
                  <td>
                    {invoice.url ? (
                      <a
                        href={invoice.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-700 hover:underline"
                      >
                        {invoice.number || invoice.id}
                      </a>
                    ) : (
                      invoice.number || invoice.id
                    )}
                  </td>
                  <td>{invoice.status}</td>
                  <td className="text-right">
                    {formatMoney(invoice.amount, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex justify-between items-center mt-6">
          {user.customer_id ? (
            <a
              href={`https://dashboard.stripe.com/customers/${user.customer_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-700 hover:underline"
            >
              Open in Stripe
            </a>
          ) : (
            <span />
          )}
          <Button type="button" variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default ArchivedDetailsModal;
